// ─── AutoChinBot Engine ────────────────────────────────────────
// Generic market-data engine for the AutoChinBot strategy type.
//
// Beyond the Chin Bot / Trailing Bot, AutoChinBot is designed to talk to
// the user through Telegram (semi-automatic + automatic order entry). The
// Telegram interaction itself is planned for a later phase — this engine
// only builds the FOUNDATION:
//   1. Connect to cTrader through the SAME shared stream / gateway used by
//      the Chin Bot (spots + trendbars), so price data flows in real time.
//   2. Fetch historical candles on init and maintain a completed-candle
//      buffer [A0, A1, ..., An] (newest closed candle first) per symbol.
//
// This is intentionally a thin, reusable data layer. Detection logic,
// order execution and Telegram proposals will layer on top of it later.
//
// Connection model (identical to Chin Bot):
//   - Subscribes to the shared cTrader SSE stream (shared-ctrader-stream)
//   - Historical candles come from the ctrader-gateway edge function
//   - One engine instance per (strategyId, accountId) — composite session key

import { supabase } from './supabase';
import { subscribeSharedStream, type StreamEvent } from './shared-ctrader-stream';
import { parseTrendbars, mergeLiveCandle, type CandleData } from './indicators';
import { buildSessionKey } from './session-key';
import { hydrateSymbolPipPositions } from './symbol-pip-position';

// ─── Types ──────────────────────────────────────────────────────

/** One completed (closed) candle entry — buffer[0] is the most recent close. */
export interface AutoChinCandle {
  timestamp: number;
  open: number;
  high: number;
  low: number;
  close: number;
}

export interface AutoChinSymbolBuffer {
  symbolId: number;
  symbolName: string;
  digits: number;
  /** cTrader ProtoOASymbol.pipPosition — the broker's authoritative pip digit index. */
  pipPosition: number | null;
  /** [A0, A1, ..., An] — newest COMPLETED candle first (forming candle excluded). */
  buffer: AutoChinCandle[];
  /** All candles including the forming one, oldest-first (used internally). */
  rawCandles: CandleData[];
  currentBid: number | null;
  currentAsk: number | null;
  lastTickTime: number;
  loaded: boolean;
}

export interface AutoChinBotSnapshot {
  symbols: Record<number, AutoChinSymbolBuffer>;
  symbolIds: number[];
  connected: boolean;
  isRunning: boolean;
  selectedAccountId: string;
  timeframe: string;
  symbolCount: number;
  loadedCount: number;
  lastTick: number;
  loading: boolean;
  lastError: string;
}

export type AutoChinLogLevel = 'info' | 'success' | 'warn' | 'error';
export type AutoChinLogFn = (level: AutoChinLogLevel, symbol: string, message: string) => void;

// ─── Constants ──────────────────────────────────────────────────

const MIN_CANDLES = 200;
const FETCH_CANDLES = 320;
const MAX_RAW_CANDLES = 1000;

const TIMEFRAME_MINUTES: Record<string, number> = {
  M1: 1, M5: 5, M15: 15, M30: 30,
  H1: 60, H4: 240, D1: 1440, W1: 10080, MN: 43200,
};

/**
 * Last-resort digits guess, used ONLY when the broker's real `digits` is not
 * supplied by the caller.
 *
 * ⚠️ Price scaling depends on the BROKER's `digits` (the gateway divides the
 * raw trendbar integers by 10^digits). Guessing wrong scales every price up or
 * down, which corrupts pip distances — e.g. a JPY pair guessed as 3 digits
 * while the broker actually uses 5 makes every body ×100, and XAU/XAG ×1000.
 * Callers MUST pass the symbol list's real `digits`; this is a fallback only.
 */
function guessDigits(symbolName: string): number {
  const upper = symbolName.toUpperCase();
  if (upper.includes('JPY')) return 3;
  if (upper.includes('XAU') || upper.includes('XAG')) return 2;
  return 5;
}

// ─── Engine Class ───────────────────────────────────────────────

export class AutoChinBotEngine {
  private accountId = '';
  private symbolIds: number[] = [];
  private symbolNames: Record<number, string> = {};
  private symbolDigits: Record<number, number> = {};
  private symbolPipPositions: Record<number, number | null> = {};
  private timeframe = 'H1';
  private buffers: Record<number, AutoChinSymbolBuffer> = {};
  private running = false;
  private connected = false;
  private lastTick = 0;
  private loading = false;
  private lastError = '';
  private listeners: Set<(s: AutoChinBotSnapshot) => void> = new Set();
  private unsubStream: (() => void) | null = null;
  private initializing = false;

  private onLog: AutoChinLogFn | null = null;

  /** One trendbar period in ms for the active timeframe. */
  private get periodMs(): number {
    return (TIMEFRAME_MINUTES[this.timeframe] || 60) * 60 * 1000;
  }

  // ─── Performance: throttled spot notify ────────────────────
  private static readonly SPOT_NOTIFY_MS = 300;

  // ─── Self-heal: throttled history resync ───────────────────
  // SSE reconnects constantly (every ~60s on Edge timeout) and a network gap
  // can drop whole candles. Re-pull history at most this often so the newest
  // CLOSED candle is always present and the buffer can never stay holed.
  private static readonly RESYNC_MIN_INTERVAL_MS = 5 * 60 * 1000;
  private lastResyncAt = 0;
  private resyncing = false;
  private spotDirty = false;
  private spotThrottleTimer: ReturnType<typeof setTimeout> | null = null;
  private snapshotDirty = true;
  private cachedSnapshot: AutoChinBotSnapshot | null = null;

  get isRunning(): boolean { return this.running; }
  get selectedAccountId(): string { return this.accountId; }
  get currentTimeframe(): string { return this.timeframe; }

  setOnLog(fn: AutoChinLogFn): void {
    this.onLog = fn;
  }

  private log(level: AutoChinLogLevel, symbol: string, message: string): void {
    if (this.onLog) {
      try { this.onLog(level, symbol, message); } catch { /* ignore */ }
    }
  }

  // ─── Subscribe / Snapshot ──────────────────────────────────

  subscribe(fn: (s: AutoChinBotSnapshot) => void): () => void {
    this.listeners.add(fn);
    fn(this.snapshot());
    return () => { this.listeners.delete(fn); };
  }

  /** Public so monitors/force-check panels can read the latest state directly. */
  snapshot(): AutoChinBotSnapshot {
    const loadedCount = Object.values(this.buffers).filter((b) => b.loaded).length;

    if (!this.snapshotDirty && this.cachedSnapshot) {
      this.cachedSnapshot.lastTick = this.lastTick;
      this.cachedSnapshot.loading = this.loading;
      this.cachedSnapshot.lastError = this.lastError;
      this.cachedSnapshot.loadedCount = loadedCount;
      return this.cachedSnapshot;
    }

    const snap: AutoChinBotSnapshot = {
      symbols: { ...this.buffers },
      symbolIds: [...this.symbolIds],
      connected: this.connected,
      isRunning: this.running,
      selectedAccountId: this.accountId,
      timeframe: this.timeframe,
      symbolCount: this.symbolIds.length,
      loadedCount,
      lastTick: this.lastTick,
      loading: this.loading,
      lastError: this.lastError,
    };

    this.cachedSnapshot = snap;
    this.snapshotDirty = false;
    this.spotDirty = false;
    return snap;
  }

  private notify(): void {
    this.snapshotDirty = true;
    const s = this.snapshot();
    this.listeners.forEach((fn) => fn(s));
  }

  private scheduleSpotNotify(): void {
    this.spotDirty = true;
    if (this.spotThrottleTimer !== null) return;

    this.spotThrottleTimer = setTimeout(() => {
      this.spotThrottleTimer = null;
      if (!this.spotDirty) return;
      this.snapshotDirty = false;
      const s = this.snapshot();
      this.listeners.forEach((fn) => fn(s));
    }, AutoChinBotEngine.SPOT_NOTIFY_MS);
  }

  // ─── Build Completed-Candle Buffer ─────────────────────────
  //
  // candles are oldest-first. A candle counts as CLOSED only when its full
  // period has elapsed: open timestamp + period <= now.
  //
  // ⚠️ cTrader stamps a trendbar with its OPEN time, and a historical response
  // may or may not include the still-forming bar. So we must NOT blindly drop
  // the last array element — doing so discards a genuinely closed candle and
  // makes A0 lag one bar behind (the "newest candle never loaded" bug).
  // excludeForming=true keeps only candles whose period is already complete.
  // Result is reversed to newest-first [A0, A1, ...].

  private buildCompletedBuffer(candles: CandleData[], excludeForming: boolean): AutoChinCandle[] {
    const cutoff = Date.now() - this.periodMs;
    const source = excludeForming
      ? candles.filter((c) => c.timestamp <= cutoff)
      : candles;
    const entries: AutoChinCandle[] = source.map((c) => ({
      timestamp: c.timestamp,
      open: c.open,
      high: c.high,
      low: c.low,
      close: c.close,
    }));
    entries.reverse();
    return entries;
  }

  // ─── Initialize ────────────────────────────────────────────

  async init(
    accountId: string,
    symbolInfos: Array<{ symbolId: number; symbolName: string; digits?: number; pipPosition?: number | null }>,
    timeframe = 'H1',
  ): Promise<void> {
    if (this.initializing) return;

    // Switching account while running → stop the old stream first.
    if (this.running && this.accountId !== accountId) {
      this.stop();
    }

    // Same account + same symbol set → no-op.
    if (this.running && this.accountId === accountId) {
      const newIds = symbolInfos.map((s) => s.symbolId);
      const current = new Set(this.symbolIds);
      const same = newIds.length === this.symbolIds.length && newIds.every((id) => current.has(id));
      if (same) return;
      this.stop();
    }

    this.initializing = true;
    this.accountId = accountId;
    this.timeframe = timeframe;
    this.loading = true;
    this.lastError = '';
    this.snapshotDirty = true;
    this.cachedSnapshot = null;
    this.notify();

    this.log('info', 'SYSTEM', `AutoChinBot initializing: ${symbolInfos.length} symbols on ${timeframe}`);

    // Resolve the broker's authoritative pipPosition for any symbol whose list
    // entry has it blank, so pip distances never fall back to a name-based guess.
    const resolvedInfos = await hydrateSymbolPipPositions(accountId, symbolInfos);

    this.symbolNames = {};
    this.symbolDigits = {};
    this.symbolPipPositions = {};
    for (const info of resolvedInfos) {
      this.symbolNames[info.symbolId] = info.symbolName;
      // Prefer the broker's real digits (from the symbol list) — this is what
      // the gateway uses to scale trendbar prices. Only guess when absent.
      this.symbolDigits[info.symbolId] =
        typeof info.digits === 'number' && info.digits > 0
          ? info.digits
          : guessDigits(info.symbolName);
      this.symbolPipPositions[info.symbolId] =
        typeof info.pipPosition === 'number' ? info.pipPosition : null;
    }
    this.symbolIds = resolvedInfos.map((s) => s.symbolId);

    await this.fetchAllHistorical();
    // History was just pulled, so don't immediately resync on the first connect.
    this.lastResyncAt = Date.now();

    this.loading = false;
    this.initializing = false;

    const loaded = Object.values(this.buffers).filter((b) => b.loaded).length;
    this.log('success', 'SYSTEM', `AutoChinBot ready: ${loaded}/${this.symbolIds.length} symbols loaded`);
    this.notify();
  }

  // ─── Fetch Historical Candles (ctrader-gateway) ────────────

  private async fetchAllHistorical(): Promise<void> {
    const numId = parseInt(this.accountId, 10);
    if (isNaN(numId)) return;

    const periodMinutes = TIMEFRAME_MINUTES[this.timeframe] || 60;
    // Add 150% lookback buffer to cover weekends / market gaps.
    const lookbackMinutes = Math.ceil(FETCH_CANDLES * periodMinutes * 2.5);
    const fromTimestamp = Date.now() - lookbackMinutes * 60 * 1000;
    const toTimestamp = Date.now();

    const results = await Promise.all(this.symbolIds.map(async (symId) => {
      const digits = this.symbolDigits[symId] || 5;
      const symName = this.symbolNames[symId] || `Symbol#${symId}`;
      try {
        const { data, error: invokeError } = await supabase.functions.invoke<{
          success: boolean;
          trendbars?: Record<string, unknown>;
          error?: string;
        }>('ctrader-gateway', {
          method: 'POST',
          body: {
            action: 'get-trendbars',
            accountId: numId,
            symbolId: symId,
            period: periodMinutes,
            fromTimestamp,
            toTimestamp,
          },
        });

        if (invokeError || !data?.success) {
          console.warn(`[AutoChinBot] trendbars failed for ${symName}: ${data?.error || invokeError?.message}`);
          return { symId, digits, candles: [] as CandleData[] };
        }

        const candles = data.trendbars ? parseTrendbars(data.trendbars, digits) : [];
        return { symId, digits, candles };
      } catch (err) {
        console.warn(`[AutoChinBot] error fetching ${symName}:`, err);
        return { symId, digits, candles: [] as CandleData[] };
      }
    }));

    for (const { symId, digits, candles } of results) {
      const previous = this.buffers[symId];
      const symName = this.symbolNames[symId] || `Symbol#${symId}`;

      const buffer = this.buildCompletedBuffer(candles, true);
      const entry: AutoChinSymbolBuffer = {
        symbolId: symId,
        symbolName: symName,
        digits,
        pipPosition: this.symbolPipPositions[symId] ?? null,
        buffer,
        rawCandles: candles,
        currentBid: previous?.currentBid ?? null,
        currentAsk: previous?.currentAsk ?? null,
        lastTickTime: previous?.lastTickTime ?? 0,
        loaded: buffer.length >= MIN_CANDLES,
      };
      this.buffers[symId] = entry;
    }
  }

  /** Re-fetch historical candles for all symbols (manual refresh / recovery). */
  async refreshBuffers(): Promise<void> {
    if (this.symbolIds.length === 0) return;
    this.loading = true;
    this.lastError = '';
    this.notify();
    await this.fetchAllHistorical();
    this.loading = false;
    this.notify();
  }

  /**
   * Self-heal the completed-candle buffers by re-pulling recent history.
   * Triggered on stream reconnect or when a live trendbar reveals a gap
   * (a missed candle). Throttled so it never hammers the gateway.
   */
  private async resyncRecent(reason: string): Promise<void> {
    if (this.symbolIds.length === 0) return;
    if (this.resyncing) return;
    const now = Date.now();
    if (now - this.lastResyncAt < AutoChinBotEngine.RESYNC_MIN_INTERVAL_MS) return;

    this.resyncing = true;
    this.lastResyncAt = now;
    try {
      this.log('info', 'SYSTEM', `Đồng bộ lại nến (${reason}) — đảm bảo nến mới nhất vào mảng`);
      await this.fetchAllHistorical();
      const loaded = Object.values(this.buffers).filter((b) => b.loaded).length;
      this.log('success', 'SYSTEM', `Đồng bộ lại xong: ${loaded}/${this.symbolIds.length} symbol sẵn sàng`);
      this.notify();
    } catch {
      /* keep the existing buffers on failure — never wipe good data */
    } finally {
      this.resyncing = false;
    }
  }

  // ─── Stream Events ─────────────────────────────────────────

  private onStreamEvent = (evt: StreamEvent): void => {
    if (!this.running) return;

    if (evt.type === 'spot') {
      this.handleSpot(evt);
    } else if (evt.type === 'trendbar') {
      this.handleTrendbar(evt);
    } else if (evt.type === 'connected') {
      this.connected = true;
      this.log('success', 'SYSTEM', 'Stream CONNECTED — receiving real-time data');
      this.notify();
      // A reconnect may have spanned a bar boundary — make sure we didn't miss it.
      void this.resyncRecent('kết nối lại stream');
    } else if (evt.type === 'disconnected' || evt.type === 'error') {
      this.connected = false;
      this.log('warn', 'SYSTEM', `Stream ${evt.type === 'disconnected' ? 'DISCONNECTED' : 'ERROR'} — waiting for reconnect`);
      this.notify();
    }
  };

  private handleSpot(evt: StreamEvent): void {
    if (evt.symbolId === null || evt.symbolId === undefined) return;
    const buf = this.buffers[evt.symbolId];
    if (!buf) return;

    if (evt.bid !== null && evt.bid !== undefined) buf.currentBid = evt.bid;
    if (evt.ask !== null && evt.ask !== undefined) buf.currentAsk = evt.ask;
    buf.lastTickTime = evt.receivedAt || Date.now();

    this.lastTick = Date.now();
    this.scheduleSpotNotify();
  }

  private handleTrendbar(evt: StreamEvent): void {
    if (evt.symbolId === null || evt.symbolId === undefined) return;
    const buf = this.buffers[evt.symbolId];
    if (!buf) return;

    const newCandle: CandleData = {
      timestamp: evt.timestamp || Date.now(),
      open: evt.open ?? 0,
      high: evt.high ?? 0,
      low: evt.low ?? 0,
      close: evt.close ?? 0,
      volume: evt.volume ?? undefined,
    };

    if (newCandle.open === 0 && newCandle.close === 0) return;

    const prevLen = buf.rawCandles.length;
    const prevLastTs = prevLen > 0 ? buf.rawCandles[prevLen - 1].timestamp : null;
    const merged = mergeLiveCandle(buf.rawCandles, newCandle);
    buf.rawCandles = merged.length > MAX_RAW_CANDLES ? merged.slice(-MAX_RAW_CANDLES) : merged;

    // Gap detection: a live bar that jumps more than one period ahead of the
    // last known candle means we missed bars (stream gap / long reconnect).
    // Self-heal by re-pulling history so the array has no holes.
    if (prevLastTs !== null && newCandle.timestamp - prevLastTs > this.periodMs * 1.5) {
      void this.resyncRecent(`thiếu nến ${buf.symbolName}`);
    }

    const needsRebuild = merged.length > prevLen || prevLen === 0;
    if (needsRebuild) {
      buf.buffer = this.buildCompletedBuffer(buf.rawCandles, true);
      buf.loaded = buf.buffer.length >= MIN_CANDLES;
    }

    buf.lastTickTime = evt.receivedAt || Date.now();
    this.lastTick = Date.now();
    this.notify();
  }

  // ─── Start / Stop ──────────────────────────────────────────

  start(): void {
    if (this.running) return;
    if (this.symbolIds.length === 0) return;

    const numId = parseInt(this.accountId, 10);
    if (isNaN(numId)) return;

    this.running = true;
    this.connected = false;

    const periodMinutes = TIMEFRAME_MINUTES[this.timeframe] || 60;

    this.unsubStream = subscribeSharedStream(
      {
        id: `autochin-bot__${this.accountId}`,
        symbolIds: this.symbolIds,
        periods: [periodMinutes],
        symbolDigits: this.symbolDigits,
        onEvent: this.onStreamEvent,
      },
      numId,
    );

    console.log(`[AutoChinBot] Started — account=${this.accountId} symbols=${this.symbolIds.length} tf=${this.timeframe}`);
    this.log('success', 'SYSTEM', `Engine STARTED: ${this.symbolIds.length} symbols streaming on ${this.timeframe}`);
    this.notify();
  }

  stop(): void {
    this.running = false;
    this.connected = false;

    if (this.spotThrottleTimer) {
      clearTimeout(this.spotThrottleTimer);
      this.spotThrottleTimer = null;
    }
    this.spotDirty = false;
    this.snapshotDirty = true;
    this.cachedSnapshot = null;

    if (this.unsubStream) {
      this.unsubStream();
      this.unsubStream = null;
    }

    console.log(`[AutoChinBot] Stopped — account=${this.accountId}`);
    this.log('info', 'SYSTEM', 'Engine STOPPED');
    this.notify();
  }

  destroy(): void {
    this.stop();
    this.listeners.clear();
    this.buffers = {};
    this.symbolIds = [];
    this.cachedSnapshot = null;
  }
}

// ─── Per-Account Singleton Registry ────────────────────────────
// One instance per (strategyId, accountId) — a strategy can run on
// multiple cTrader accounts simultaneously, each with its own stream.

const engines = new Map<string, AutoChinBotEngine>();

export function getOrCreateAutoChinEngine(strategyId: string, accountId: string): AutoChinBotEngine {
  const key = buildSessionKey(strategyId, accountId);
  let eng = engines.get(key);
  if (!eng) {
    eng = new AutoChinBotEngine();
    engines.set(key, eng);
  }
  return eng;
}

export function getAutoChinEngine(strategyId: string, accountId: string): AutoChinBotEngine | undefined {
  return engines.get(buildSessionKey(strategyId, accountId));
}

export function getAllAutoChinEngines(): Map<string, AutoChinBotEngine> {
  return engines;
}

export function destroyAutoChinEngine(strategyId: string, accountId: string): void {
  const key = buildSessionKey(strategyId, accountId);
  const eng = engines.get(key);
  if (eng) {
    eng.destroy();
    engines.delete(key);
  }
}